// packages/kb-view/src/reader-links.ts
//
// LAT-1605 (LAT-1600 S5) — the reader base path is a PARAMETER, not a
// constant. Guide pages are authored for the repo and for agents, where a
// sibling link reads `(knowledge-bases.md)`; a reader that serves them at
// `/help/<slug>` or `/docs/<slug>` has to rewrite those for display. The rule
// is identical on both surfaces, so only the base differs.

import { parseSourceLineRangeFragment } from "./source-span.js";
import { resolveKbLinkPath, safePercentDecode } from "./kb-link-target.js";
import { classifyPublishedKbFile } from "./kb-family.js";

/** A relative, known-shape cross-link target: `<slug>.md`, `help/<slug>.md`,
 *  or `wiki/<slug>.md`. Deliberately narrow — no scheme, no leading slash,
 *  one optional `help/` or `wiki/` segment — so external links and anchors
 *  that merely contain `.md` are never touched.
 *
 *  ADR 0058 §10: fragments of the source-span grammar (`#L<start>`,
 *  `#L<start>-L<end>`) are preserved through the rewrite. Other fragment
 *  shapes stay on the original target, byte-identical.
 */
const RELATIVE_READER_MD_LINK =
  /(\]\(\s*)((?:\/)?(?:(?:\.{1,2}|[a-z0-9-]+)\/)*[a-z0-9-]+\.md)(#[^\s)"']+)?(\s*(?:"[^"]*")?\s*\))/gi;

/** Resolve one rendered Markdown link through the published-KB page grammar. */
export function publishedKbReaderHrefForTarget(
  target: string,
  basePath: string,
  sourcePath: string,
): string {
  if (
    target.startsWith("#") ||
    target.startsWith("//") ||
    /^[a-z][a-z0-9+.-]*:/i.test(target)
  ) {
    return target;
  }
  const hash = target.indexOf("#");
  const pathTarget = hash < 0 ? target : target.slice(0, hash);
  const fragment = hash < 0 ? "" : target.slice(hash);
  if (pathTarget.includes("?")) return target;

  const resolved = resolveKbLinkPath(safePercentDecode(pathTarget), sourcePath);
  if (resolved.kind === "invalid") return target;
  const classification = classifyPublishedKbFile(resolved.path);
  if (classification.outcome === "home") return `${basePath}${fragment}`;
  if (classification.outcome !== "page") return target;
  return `${basePath}/${classification.slug}${fragment}`;
}

/**
 * Rewrite a page's repo-relative sibling links (`(<slug>.md)` /
 * `(help/<slug>.md)` / `(wiki/<slug>.md)`) to reader routes under `basePath`
 * (`/help`, `/docs`, `/kb/<team>/<kb>`, …) for display. External http(s)
 * links, in-page anchors, and already-absolute links are left byte-identical.
 * Span fragments survive; other fragments leave the whole target untouched.
 */
export function rewriteReaderLinks(
  markdown: string,
  basePath: string,
  sourcePath?: string,
): string {
  return markdown.replace(
    RELATIVE_READER_MD_LINK,
    (
      _match,
      open: string,
      target: string,
      fragment: string | undefined,
      close: string,
      offset: number,
    ) => {
      const labelStart = markdown.lastIndexOf("[", offset);
      if (labelStart > 0 && markdown[labelStart - 1] === "!") return _match;
      if (fragment && parseSourceLineRangeFragment(fragment) === null) {
        return `${open}${target}${fragment}${close}`;
      }
      if (sourcePath !== undefined) {
        const href = publishedKbReaderHrefForTarget(
          `${target}${fragment ?? ""}`,
          basePath,
          sourcePath,
        );
        return `${open}${href}${close}`;
      }
      if (
        target.startsWith("/") ||
        (target.includes("/") && !/^(?:help|wiki)\//i.test(target))
      ) {
        return `${open}${target}${fragment ?? ""}${close}`;
      }
      const slug = target
        .replace(/^(?:help|wiki)\//i, "")
        .replace(/\.md$/i, "");
      return `${open}${basePath}/${slug}${fragment ?? ""}${close}`;
    },
  );
}
